/**
 * Utility functions for handling metadata in the extraction jobs list
 */

/**
 * Formats a metadata value for display in the data grid
 * @param {any} value - The raw metadata value
 * @param {string} dataType - The expected data type
 * @returns {string} Formatted value for display
 */
export const formatMetadataValue = (value, dataType = 'string') => {
  if (value === null || value === undefined || value === '') {
    return 'N/A';
  }

  switch (dataType.toLowerCase()) {
    case 'number':
    case 'decimal':
      const numValue = parseFloat(value);
      return isNaN(numValue) ? 'N/A' : numValue.toLocaleString();
    
    case 'currency':
      const currValue = parseFloat(value);
      return isNaN(currValue) ? 'N/A' : new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(currValue);
    
    case 'date':
      try {
        const dateValue = new Date(value);
        return isNaN(dateValue.getTime()) ? String(value) : dateValue.toLocaleDateString();
      } catch {
        return String(value);
      }
    
    case 'boolean':
      if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
      }
      const strValue = String(value).toLowerCase();
      return ['true', 'yes', '1'].includes(strValue) ? 'Yes' : 'No';
    
    default:
      return String(value);
  }
};

/**
 * Determines the appropriate column width based on field type and content
 * @param {string} fieldName - The field name
 * @param {string} dataType - The data type
 * @returns {number} Recommended column width in pixels
 */
export const getColumnWidth = (fieldName, dataType = 'string') => {
  // Base width on field name length
  const baseWidth = Math.max(fieldName.length * 8, 100);
  
  switch (dataType.toLowerCase()) {
    case 'number':
    case 'decimal':
      return Math.max(baseWidth, 120);
    
    case 'currency':
      return Math.max(baseWidth, 130);
    
    case 'date':
      return Math.max(baseWidth, 140);
    
    case 'boolean':
      return Math.max(baseWidth, 80);
    
    default:
      // For strings, cap at reasonable width
      return Math.min(Math.max(baseWidth, 150), 300);
  }
};

/**
 * Filters metadata fields based on search criteria
 * @param {Array} fields - Array of metadata fields
 * @param {string} searchTerm - Search term to filter by
 * @returns {Array} Filtered fields
 */
export const filterMetadataFields = (fields, searchTerm) => {
  if (!searchTerm) return fields;
  
  const term = searchTerm.toLowerCase();
  return fields.filter(field => 
    field.name.toLowerCase().includes(term) ||
    (field.description && field.description.toLowerCase().includes(term))
  );
};

/**
 * Groups metadata fields by type
 * @param {Array} fields - Array of metadata fields
 * @returns {Object} Fields grouped by type
 */
export const groupFieldsByType = (fields) => {
  return fields.reduce((groups, field) => {
    const type = field.fieldType || 'Other';
    if (!groups[type]) {
      groups[type] = [];
    }
    groups[type].push(field);
    return groups;
  }, {});
};

/**
 * Validates metadata filter values
 * @param {Object} filters - Filter object
 * @returns {Object} Validated filters
 */
export const validateMetadataFilters = (filters) => {
  if (!filters || typeof filters !== 'object') {
    return {};
  }
  
  const validatedFilters = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (key && value !== null && value !== undefined && value !== '') {
      validatedFilters[key] = String(value).trim();
    }
  });
  
  return validatedFilters;
};

export default {
  formatMetadataValue,
  getColumnWidth,
  filterMetadataFields,
  groupFieldsByType,
  validateMetadataFilters
};